import { useRef, useState } from 'react'
import { Link } from 'react-router'
import { useSendTemplate, useTemplateTerms } from '../api/inbox'
import { useTemplates } from '../api/templates'
import { RequestFailed } from '../api/client'
import { newId } from '../lib/format'
import { TemplatePreview } from './TemplatePreview'
import { hasLinkVariable, sendable } from '../lib/templates'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'

const currencySign: Record<string, string> = { INR: '₹' }

function price(amount: string | undefined, currency: string | undefined): string | undefined {
  if (!amount || !currency) return undefined
  return `${currencySign[currency] ?? `${currency} `}${Number(amount)}`
}

// Sends an approved template: the only way to reach a customer once reply
// time has ended, and a way to send a prepared message any time. The price
// and any rule that blocks the send are shown before Send, from the server.
export function TemplateSender({ conversationId, customer }: { conversationId: string; customer: string }) {
  const lib = useTemplates()
  const terms = useTemplateTerms(conversationId)
  const send = useSendTemplate(conversationId)
  const [templateId, setTemplateId] = useState<string | undefined>(undefined)
  const [header, setHeader] = useState('')
  const [body, setBody] = useState<string[]>([])
  const [button, setButton] = useState('')
  // One key per prepared template; a retry after a failure reuses it.
  const key = useRef(newId())

  if (lib.isPending || terms.isPending) {
    return (
      <div role="status" aria-label="Loading templates" className="flex flex-col gap-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    )
  }
  if (lib.isError || terms.isError) {
    return <p role="alert" className="text-sm text-destructive">{((lib.error ?? terms.error) as RequestFailed).message}</p>
  }

  const options = lib.data.templates.filter(sendable)
  if (options.length === 0) {
    return (
      <p className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
        No approved templates yet. They are made and synced on the <Link to="/templates" className="font-semibold text-primary-text underline underline-offset-2">Templates</Link> page; Meta must approve one before it can be sent.
      </p>
    )
  }
  if (!terms.data.allowed) {
    return <p className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">{terms.data.blockedReason}</p>
  }

  const t = options.find((o) => o.id === templateId)
  const linkVar = t ? hasLinkVariable(t) : false
  const values = Array.from({ length: t?.bodyVariables ?? 0 }, (_, i) => body[i] ?? '')
  const marketingBlocked = t?.category === 'marketing' && !terms.data.marketingAllowed
  const cost = t ? price(terms.data.prices[t.category], terms.data.currency) : undefined
  const missing = !t || values.some((v) => !v.trim()) || (t.headerVariable && !header.trim()) || (linkVar && !button.trim())
  const error = send.error as RequestFailed | null
  const fieldError = (k: string) => error?.fields?.[k]

  // A fresh form and a fresh key for the next template. After a send the
  // mutation's success stays visible; choosing a template clears it.
  const clear = (id: string | undefined) => {
    setTemplateId(id)
    setHeader('')
    setBody([])
    setButton('')
    key.current = newId()
  }
  const choose = (id: string) => {
    clear(id)
    send.reset()
  }
  const submit = () => {
    if (!t || missing || marketingBlocked || send.isPending) return
    send.mutate(
      {
        clientMessageId: key.current,
        templateId: t.id,
        bodyValues: values.map((v) => v.trim()),
        ...(t.headerVariable ? { headerValue: header.trim() } : {}),
        ...(linkVar ? { buttonValue: button.trim() } : {}),
      },
      { onSuccess: () => clear(undefined) },
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="template-pick">Template</Label>
        <Select value={templateId ?? ''} onValueChange={choose}>
          <SelectTrigger id="template-pick" className="w-full"><SelectValue placeholder="Choose an approved template" /></SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.id} value={o.id}>
                {o.name} ({o.language}, {o.category})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {t && (
        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_300px]">
          <div className="flex min-w-0 flex-col gap-3">
            {t.headerVariable && (
              <Value id="tv-header" label="Header {{1}}" value={header} onChange={setHeader} error={fieldError('headerValue')} />
            )}
            {values.map((v, i) => (
              <Value
                key={i}
                id={`tv-body-${i}`}
                label={`{{${i + 1}}}`}
                value={v}
                onChange={(x) => setBody(values.map((y, j) => (j === i ? x : y)))}
              />
            ))}
            {fieldError('bodyValues') && <p className="text-sm text-destructive">{fieldError('bodyValues')}</p>}
            {linkVar && <Value id="tv-link" label="End of the link, {{1}}" value={button} onChange={setButton} error={fieldError('buttonValue')} />}
          </div>
          <TemplatePreview
            header={t.header}
            headerExample={header}
            body={t.body}
            bodyExamples={values}
            footer={t.footer}
            buttons={t.buttons}
            className="p-3"
          />
        </div>
      )}

      {t && (
        <p className="text-xs text-muted-foreground">
          {marketingBlocked
            ? terms.data.marketingBlockedReason
            : cost
              ? `Costs ${cost} per delivered message (${t.category}, from the rate card for this customer's country).`
              : `No price on file for this customer's country. Meta bills its own ${t.category} rate.`}
        </p>
      )}
      {error && !error.fields && <p role="alert" className="text-sm text-destructive">{error.message}</p>}
      {send.isSuccess && !t && <p role="status" className="text-sm text-primary-text">Template queued for {customer}.</p>}

      <div className="flex justify-end">
        <Button type="button" disabled={!t || missing || marketingBlocked || send.isPending} onClick={submit}>
          {send.isPending ? 'Sending' : `Send template to ${customer}`}
        </Button>
      </div>
    </div>
  )
}

function Value({ id, label, value, onChange, error }: { id: string; label: string; value: string; onChange: (v: string) => void; error?: string | undefined }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} maxLength={1024} onChange={(e) => onChange(e.target.value)} />
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}
